import React, { useEffect, useState } from "react";
import { validateLoggedIn, getSession } from "../utils";
import {
    TextField, Paper, CircularProgress, Typography, Box, Grid, Button, Alert, MenuItem,
} from "@mui/material";
import NavBar from "../navbar";
import { useNavigate, useParams } from "react-router-dom";
import config from "../config";
import * as Sentry from "@sentry/react";

const EditSubscriptionPlan = () => {
    const { plan_id } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [originalData, setOriginalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(config.apiBaseUrl + `/admin/subscription-plans`, {
                    headers: { "Content-Type": "application/json", "user-authorization": getSession().token },
                });
                if (!response.ok) throw new Error(`Error: ${response.status}`);
                const all = await response.json();
                const found = all.find(p => p.id === plan_id);
                if (!found) throw new Error("Subscription plan not found");
                setData(found);
                setOriginalData(found);
            } catch (err) {
                Sentry.captureException(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [plan_id]);

    const validate_logged_in = validateLoggedIn();
    if (validate_logged_in) return validate_logged_in;

    const handleChange = (field, value) => setData({ ...data, [field]: value });

    const handleSave = async () => {
        try {
            setSaving(true); setError(null);
            if (!data.name.trim()) { setError("Name is required"); setSaving(false); return; }

            const payload = {
                id: data.id,
                name: data.name,
                description: data.description || null,
                stripe_price_id: data.stripe_price_id,
                stripe_product_id: data.stripe_product_id,
                monthly_credits: parseInt(data.monthly_credits),
                price_cents: parseInt(data.price_cents),
                currency: data.currency,
                is_active: data.is_active,
                is_public: data.is_public,
                sort_order: parseInt(data.sort_order),
                token_plan_id: data.token_plan_id || null,
                user_id: data.user_id || null,
            };
            const response = await fetch(config.apiBaseUrl + "/admin/subscription-plans", {
                method: "PUT",
                headers: { "Content-Type": "application/json", "user-authorization": getSession().token },
                body: JSON.stringify(payload),
            });
            if (!response.ok) { const err = await response.json(); throw new Error(err.detail || `Error: ${response.status}`); }
            setSuccess(true);
            setOriginalData(await response.json());
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            Sentry.captureException(err); setError(err.message);
        } finally { setSaving(false); }
    };

    const handleDeactivate = async () => {
        if (!window.confirm("Deactivate this subscription plan? (soft delete)")) return;
        try {
            const response = await fetch(config.apiBaseUrl + `/admin/subscription-plans?plan_id=${plan_id}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json", "user-authorization": getSession().token },
            });
            if (!response.ok) throw new Error(`Error: ${response.status}`);
            alert("Subscription plan deactivated!");
            navigate("/subscription-plans");
        } catch (err) { Sentry.captureException(err); alert(`Failed: ${err.message}`); }
    };

    const hasChanges = data && originalData && JSON.stringify(data) !== JSON.stringify(originalData);
    if (loading) return <div style={{ textAlign: "center", padding: "20px" }}><CircularProgress /></div>;
    if (error && !data) return <div style={{ textAlign: "center", padding: "20px", color: "red" }}><p>{error}</p></div>;

    return (
        <>
            <NavBar />
            <Box sx={{ maxWidth: 900, margin: "auto", padding: 3 }}>
                <Paper sx={{ padding: 3 }}>
                    <Typography variant="h5" gutterBottom>Edit Subscription Plan</Typography>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>Updated successfully!</Alert>}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth margin="normal" label="ID" value={data?.id || ""} InputProps={{ readOnly: true }} />
                            <TextField fullWidth required margin="normal" label="Name" value={data?.name || ""} onChange={(e) => handleChange("name", e.target.value)} disabled={saving} />
                            <TextField fullWidth margin="normal" label="Description" multiline rows={3} value={data?.description || ""} onChange={(e) => handleChange("description", e.target.value)} disabled={saving} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth margin="normal" label="Stripe Price ID" value={data?.stripe_price_id || ""} onChange={(e) => handleChange("stripe_price_id", e.target.value)} disabled={saving} />
                            <TextField fullWidth margin="normal" label="Stripe Product ID" value={data?.stripe_product_id || ""} onChange={(e) => handleChange("stripe_product_id", e.target.value)} disabled={saving} />
                            <TextField fullWidth margin="normal" label="Currency" value={data?.currency || ""} onChange={(e) => handleChange("currency", e.target.value)} disabled={saving} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth margin="normal" label="Price (cents)" type="number" inputProps={{ min: 0 }} value={data?.price_cents || 0} onChange={(e) => handleChange("price_cents", parseInt(e.target.value) || 0)} disabled={saving} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth margin="normal" label="Monthly Credits" type="number" inputProps={{ min: 0 }} value={data?.monthly_credits || 0} onChange={(e) => handleChange("monthly_credits", parseInt(e.target.value) || 0)} disabled={saving} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth margin="normal" label="Sort Order" type="number" value={data?.sort_order || 0} onChange={(e) => handleChange("sort_order", parseInt(e.target.value) || 0)} disabled={saving} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField select fullWidth margin="normal" label="Active" value={data?.is_active ? "true" : "false"} onChange={(e) => handleChange("is_active", e.target.value === "true")} disabled={saving}>
                                <MenuItem value="true">Yes</MenuItem>
                                <MenuItem value="false">No</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField select fullWidth margin="normal" label="Public" value={data?.is_public ? "true" : "false"} onChange={(e) => handleChange("is_public", e.target.value === "true")} disabled={saving}>
                                <MenuItem value="true">Yes</MenuItem>
                                <MenuItem value="false">No</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth margin="normal" label="Token Plan ID" value={data?.token_plan_id || ""} onChange={(e) => handleChange("token_plan_id", e.target.value || null)} disabled={saving} helperText="Leave empty for none" />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth margin="normal" label="User ID" value={data?.user_id || ""} onChange={(e) => handleChange("user_id", e.target.value || null)} disabled={saving} helperText="Leave empty for none" />
                        </Grid>
                    </Grid>
                    <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !hasChanges}>{saving ? "Saving..." : "Save Changes"}</Button>
                        <Box>
                            <Button variant="outlined" color="secondary" onClick={() => setData(originalData)} disabled={saving || !hasChanges} sx={{ mr: 2 }}>Discard</Button>
                            <Button variant="outlined" color="error" onClick={handleDeactivate} disabled={saving} sx={{ mr: 2 }}>Deactivate</Button>
                            <Button variant="outlined" onClick={() => navigate("/subscription-plans")} disabled={saving}>Back</Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </>
    );
};

export default EditSubscriptionPlan;
